import { useTranslation } from "react-i18next";
import { MoreHorizontal, Paperclip, Pin, StickyNote } from "lucide-react";
import { Avatar } from "@/components/ui/avatar";
import type { DialogSummary } from "@/lib/inbox";
import { formatShortTime } from "@/lib/format";
import { cn } from "@/lib/cn";
import { LeadTag } from "./LeadStatus";
import { peerDisplayName } from "./peer";

export interface DialogListItemProps {
  dialog: DialogSummary;
  selected: boolean;
  onClick: () => void;
  /**
   * Open the row's menu at this point of the screen — asked for by a right
   * click on the row, or by its "⋯" (the only way on a touch screen).
   */
  onMenu?: (at: { x: number; y: number }) => void;
  /**
   * Optional last-message preview (derived from the messages cache by the
   * parent — the dialog summary itself carries no message text). Falls back
   * to the "via account" meta line when absent.
   */
  preview?: string | null;
  /**
   * What the latest message carries — "Photo", "Voice message", a file's name.
   * Shown with a paperclip, in place of the text when there is none.
   */
  previewMedia?: string | null;
}

/**
 * One row in the inbox dialog list. Shared by InboxPage (triage) and
 * InboxDrawer (quick answer) so both render identical rows. Min font 12px
 * per DESIGN-SYSTEM §3.3 (no more text-[10px]).
 */
export function DialogListItem({
  dialog,
  selected,
  onClick,
  onMenu,
  preview,
  previewMedia,
}: DialogListItemProps) {
  const { t } = useTranslation();
  const name = peerDisplayName(dialog);
  const time = dialog.last_message_at
    ? formatShortTime(dialog.last_message_at)
    : "";
  const via = dialog.account_label
    ? `${t("inbox.via")} ${dialog.account_label}`
    : null;

  return (
    <li
      className="group relative"
      onContextMenu={
        onMenu &&
        ((e) => {
          e.preventDefault();
          onMenu({ x: e.clientX, y: e.clientY });
        })
      }
    >
      <button
        type="button"
        onClick={onClick}
        aria-current={selected ? "true" : undefined}
        className={cn(
          "w-full rounded-lg border px-3 py-2.5 text-left transition-colors",
          selected
            ? "border-accent/40 bg-accent/15"
            : "border-transparent hover:bg-bg-elevated/50",
        )}
      >
        <div className="flex items-start gap-2.5">
          <Avatar
            name={name}
            src={dialog.has_avatar ? `/api/inbox/dialogs/${dialog.id}/avatar` : null}
            colorKey={String(dialog.peer_tg_id)}
            size={36}
            className="mt-0.5"
          />
          <div className="min-w-0 flex-1">
            <div className="flex items-baseline justify-between gap-2">
              <p className="min-w-0 flex-1 truncate text-sm font-medium text-fg-primary">
                {name}
              </p>
              {dialog.notes_count > 0 && (
                <StickyNote
                  size={12}
                  strokeWidth={2}
                  aria-label={t("inbox.notes.has", { count: dialog.notes_count })}
                  className="shrink-0 self-center text-fg-muted"
                />
              )}
              {dialog.pinned && (
                <Pin
                  size={12}
                  strokeWidth={2}
                  aria-label={t("inbox.pinned")}
                  className="shrink-0 self-center text-fg-muted"
                />
              )}
              {time && (
                <span
                  className={cn(
                    "shrink-0 text-xs tabular-nums text-fg-muted",
                    // The "⋯" takes this corner: under a mouse while the row is
                    // hovered, on a touch screen always.
                    onMenu &&
                      "[@media(hover:hover)]:group-hover:invisible [@media(hover:none)]:mr-7",
                  )}
                >
                  {time}
                </span>
              )}
            </div>

            <div className="mt-0.5 flex items-center justify-between gap-2">
              <p className="flex min-w-0 flex-1 items-center gap-1 truncate text-xs text-fg-muted">
                {previewMedia && (
                  <Paperclip
                    size={12}
                    strokeWidth={2}
                    aria-hidden="true"
                    className="shrink-0"
                  />
                )}
                <span className="truncate">
                  {preview && preview.trim() ? preview : (previewMedia ?? via ?? "")}
                </span>
              </p>
              <LeadTag dialog={dialog} className="shrink-0" />
              {dialog.unread_count > 0 && (
                <span className="shrink-0 rounded-full bg-accent px-2 py-0.5 text-xs font-medium leading-none text-fg-on-accent">
                  {dialog.unread_count > 99 ? "99+" : dialog.unread_count}
                </span>
              )}
            </div>

            {/* Keep the via-account context visible even when a preview is shown. */}
            {preview && via && (
              <p className="mt-0.5 truncate text-xs text-fg-muted/80">{via}</p>
            )}
          </div>
        </div>
      </button>
      {onMenu && (
        <button
          type="button"
          aria-label={t("inbox.row_menu")}
          aria-haspopup="menu"
          onClick={(e) => {
            const r = e.currentTarget.getBoundingClientRect();
            onMenu({ x: r.left, y: r.bottom });
          }}
          className="absolute right-2 top-1.5 rounded-md p-1 text-fg-muted opacity-0 transition-opacity hover:bg-bg-elevated hover:text-fg-primary focus-visible:bg-bg-elevated focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent [@media(hover:hover)]:group-hover:opacity-100 [@media(hover:none)]:opacity-100"
        >
          <MoreHorizontal size={16} strokeWidth={2} aria-hidden="true" />
        </button>
      )}
    </li>
  );
}
DialogListItem.displayName = "DialogListItem";
